import React, { useState, useEffect } from 'react';
import JSZip from 'jszip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import JsonView from 'react18-json-view';
import { Tabs, Button, MessagePlugin } from 'tdesign-react';

const { TabPanel } = Tabs;

/**
 * 文档解析结果渲染器
 * 下载 ResultUrl 的 zip，解压后展示 markdown 和 JSON 内容
 */
const DocParseRenderer = ({ params, request }) => {
  const [loading, setLoading] = useState(false);
  const [mdContent, setMdContent] = useState('');
  const [jsonContent, setJsonContent] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('markdown');

  const resultUrl = params?.ResultUrl;

  useEffect(() => {
    if (!resultUrl) return;
    parseZip(resultUrl);
  }, [resultUrl]);

  const parseZip = async (url) => {
    setLoading(true);
    setError('');
    setMdContent('');
    setJsonContent(null);

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`下载失败: ${res.status}`);

      const blob = await res.blob();
      const zip = await JSZip.loadAsync(blob);

      // 查找 markdown 文件
      let mdFile = null;
      let jsonFile = null;

      zip.forEach((relativePath, file) => {
        if (!file.dir) {
          const lower = relativePath.toLowerCase();
          if (lower.endsWith('.md') && !mdFile) {
            mdFile = file;
          } else if (lower.endsWith('.json') && !jsonFile) {
            jsonFile = file;
          }
        }
      });

      if (mdFile) {
        const text = await mdFile.async('text');
        setMdContent(text);
      }

      if (jsonFile) {
        const text = await jsonFile.async('text');
        try {
          setJsonContent(JSON.parse(text));
        } catch {
          setJsonContent(text);
        }
      }

      if (!mdFile && !jsonFile) {
        setError('zip中未找到 .md 或 .json 文件');
      }
    } catch (err) {
      console.error('[DocParse] 解析zip失败:', err);
      setError(err.message || '解析结果失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (content) => {
    if (!content) return;
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    navigator.clipboard.writeText(text).then(() => {
      MessagePlugin.success('已复制到剪贴板');
    }).catch(() => {
      MessagePlugin.error('复制失败');
    });
  };

  if (!resultUrl) return null;

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>正在解析结果文件...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ color: '#e34d59', marginBottom: '12px' }}>解析失败: {error}</div>
        <Button variant="outline" size="small" onClick={() => parseZip(resultUrl)}>重试</Button>
        <a
          href={resultUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: '12px', color: '#4A70FF', fontSize: '14px' }}
        >
          直接下载结果文件
        </a>
      </div>
    );
  }

  // 根据请求的 ResultType 决定显示哪些内容
  // 1=JSON, 2=Markdown, 3=XML, 9=全部
  const resultType = request?.ResultType ?? 9;
  const showMarkdown = mdContent && (resultType === 2 || resultType === 9);
  const showJson = jsonContent && (resultType === 1 || resultType === 9);
  const hasBothTabs = showMarkdown && showJson;

  // 当前展示的内容（用于复制）
  const currentContent = hasBothTabs
    ? (activeTab === 'markdown' ? mdContent : jsonContent)
    : (showMarkdown ? mdContent : jsonContent);

  const renderMarkdown = () => (
    <div className="markdown_container" style={{ padding: '12px' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
      >
        {mdContent}
      </ReactMarkdown>
    </div>
  );

  const renderJson = () => (
    <div style={{ padding: '12px' }}>
      {typeof jsonContent === 'string' ? (
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{jsonContent}</pre>
      ) : (
        <JsonView
          src={jsonContent}
          theme="winter-is-coming"
          displayDataTypes={false}
        />
      )}
    </div>
  );

  return (
    <div className="doc-parse-result">
      {hasBothTabs ? (
        <Tabs value={activeTab} onChange={setActiveTab} style={{ width: '100%' }}>
          <TabPanel value="markdown" label="Markdown">{renderMarkdown()}</TabPanel>
          <TabPanel value="json" label="JSON">{renderJson()}</TabPanel>
        </Tabs>
      ) : (
        <>
          {showMarkdown && renderMarkdown()}
          {showJson && renderJson()}
        </>
      )}

      {/* 底部操作栏：复制 + 下载，左对齐 */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px' }}>
        <Button
          theme="default"
          variant="outline"
          onClick={() => handleCopy(currentContent)}
        >
          复制结果
        </Button>
        <Button
          theme="default"
          variant="outline"
          onClick={() => window.open(resultUrl, '_blank')}
        >
          下载完整结果(zip)
        </Button>
      </div>
    </div>
  );
};

export default DocParseRenderer;
